import { useMemo } from 'react';
import { useIntl } from 'react-intl';

import { useGetNewDatasetReleaseByIdQuery } from '@verantos-dev/data-access-datasets';
import type { InfoTileProps } from '@verantos-dev/shared-components';
import {
  dataTypeFormatter,
  formatDate,
  formatDateAsString,
  formatNumericValue,
} from '@verantos-dev/shared-utils';

export const useDatasetDetailsModal = (releaseId: string, onClose: () => void) => {
  const intl = useIntl();
  const {
    data: datasetDetails,
    isLoading,
    isFetching,
    error,
  } = useGetNewDatasetReleaseByIdQuery(releaseId);

  const details: InfoTileProps[] = useMemo(() => {
    if (!datasetDetails?.data) return [];
    const {
      releaseDate,
      patientCount,
      dataStartDate = '',
      dataEndDate = '',
      dataTypes,
    } = datasetDetails.data;

    const formattedDetails: InfoTileProps[] = [
      {
        id: 'releaseDate',
        icon: 'calendarMonth',
        detailName: intl.formatMessage({ id: 'projectsList.datasetModal.releaseDate' }),
        detailValue: releaseDate ? formatDate(releaseDate, 'dateNumeric') : '--',
      },
      {
        id: 'dateRange',
        icon: 'calendarRange',
        detailName: intl.formatMessage({ id: 'projectsList.datasetModal.dateRange' }),
        detailValue:
          dataStartDate && dataEndDate
            ? `${formatDateAsString(dataStartDate)}\u2014${formatDateAsString(dataEndDate)}`
            : '--',
      },
      {
        id: 'patientCount',
        icon: 'counter',
        detailName: intl.formatMessage({ id: 'projectsList.datasetModal.patientCount' }),
        detailValue: patientCount ? formatNumericValue(patientCount) : '--',
      },
      {
        id: 'desc',
        icon: 'description',
        detailName: intl.formatMessage({ id: 'projectsList.datasetModal.dataTypes' }),
        detailValue: dataTypeFormatter((dataTypes || '').toString().split(',')),
      },
    ];

    return formattedDetails.filter(({ detailValue }) => !!detailValue);
  }, [datasetDetails, intl]);

  return {
    subTitle: intl.formatMessage({ id: 'projectsList.datasetModal.title' }),
    closeDatasetDetailsModal: onClose,
    datasetDetailsInfo: details,
    isLoading,
    isFetching,
    noDatasetDetailsText: intl.formatMessage({ id: 'projectsList.datasetModal.noDatasets' }),
    error,
  };
};
